/**
 * A titled, collapsible content region with a disclosure chevron.
 *
 * Generic — no dependency on any particular data shape. Used for grouping
 * secondary content (e.g. the "More Details" block on the question view) so the
 * primary content stays uncluttered. Uncontrolled by default via
 * `defaultExpanded`; pass `expanded` + `onToggle` to control it.
 */
import { useId, useState, type ReactNode } from 'react';
import './CollapsibleSection.css';

export interface CollapsibleSectionProps {
  title: ReactNode;
  /** Optional content shown to the right of the title (e.g. a count). */
  titleAccessory?: ReactNode;
  defaultExpanded?: boolean;
  /** Controlled expansion — supply with `onToggle`. Overrides internal state. */
  expanded?: boolean;
  onToggle?: (next: boolean) => void;
  children: ReactNode;
  className?: string;
  /**
   * How the header carries itself.
   *   'inline'  — a disclosure inside a page (the default): 15px, rule above it.
   *   'section' — a top-level page section, sized like the `h2` headings it
   *               sits among, with the accessory beside the title rather than
   *               pushed to the far edge.
   */
  variant?: 'inline' | 'section';
}

export function CollapsibleSection({
  title,
  titleAccessory,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onToggle,
  children,
  className,
  variant = 'inline',
}: CollapsibleSectionProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;
  const bodyId = useId();

  const toggle = () => {
    const next = !expanded;
    if (!isControlled) setInternalExpanded(next);
    onToggle?.(next);
  };

  return (
    <div className={`collapsible-section collapsible-section--${variant} ${className ?? ''}`}>
      <button
        type="button"
        className="collapsible-section-toggle"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={bodyId}
      >
        <span className={`collapsible-section-chevron ${expanded ? 'expanded' : ''}`} aria-hidden="true">▸</span>
        <span className="collapsible-section-title">{title}</span>
        {titleAccessory != null && <span className="collapsible-section-accessory">{titleAccessory}</span>}
      </button>
      {expanded && (
        <div id={bodyId} className="collapsible-section-body">
          {children}
        </div>
      )}
    </div>
  );
}
